import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Clock, CheckCircle2 } from "lucide-react";
import { http } from "../lib/api";

const PROJECT_TYPES = ["Web App", "Marketing Site", "E-commerce", "Mobile", "Platform", "Something else"];
const BUDGETS = ["Under $5k", "$5k – $15k", "$15k – $40k", "$40k+", "Not sure yet"];

const field =
  "w-full rounded-sm border border-input bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-[#FF4D00]";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", project_type: "", budget_range: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const [days, setDays] = useState([]);
  const [tz, setTz] = useState("ET");
  const [activeDay, setActiveDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [booking, setBooking] = useState({ name: "", email: "", topic: "" });
  const [bookingBusy, setBookingBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    http
      .get("/availability")
      .then(({ data }) => {
        setDays(data.days);
        setTz(data.timezone);
        setActiveDay(data.days[0]?.date ?? null);
      })
      .catch(() => toast.error("Could not load available times."));
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!form.project_type || !form.budget_range) {
      toast.error("Pick a project type and a budget range.");
      return;
    }
    setSending(true);
    try {
      await http.post("/inquiries", form);
      setSent(true);
      setForm({ name: "", email: "", project_type: "", budget_range: "", message: "" });
      toast.success("Inquiry sent. I'll reply within two business days.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSending(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!slot || !activeDay) {
      toast.error("Pick a time slot first.");
      return;
    }
    setBookingBusy(true);
    try {
      const { data } = await http.post("/bookings", {
        ...booking,
        slot_date: activeDay,
        slot_time: slot,
      });
      setConfirmed(data);
      toast.success("Call booked. Check your inbox for the confirmation.");
      const res = await http.get("/availability");
      setDays(res.data.days);
      setSlot(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not book that slot.");
    } finally {
      setBookingBusy(false);
    }
  };

  const day = days.find((d) => d.date === activeDay);

  return (
    <div className="mx-auto max-w-6xl px-6 pt-16 sm:px-10 sm:pt-24" data-testid="contact-page">
      <p className="font-mono-label mb-5">Contact</p>
      <h1 className="max-w-2xl text-4xl font-extrabold leading-[1.05] sm:text-5xl">
        Tell me about the project.
      </h1>
      <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground">
        Send a note or book a 30-minute call. Either way you'll hear back from me
        directly, usually within two business days.
      </p>

      <div className="mt-16 grid gap-16 lg:grid-cols-2 lg:gap-20">
        <section data-testid="inquiry-section">
          <div className="mb-8 flex items-center gap-3">
            <Mail size={16} className="text-[#FF4D00]" />
            <p className="font-mono-label">Send an inquiry</p>
          </div>

          {sent ? (
            <div className="border border-border bg-card p-8" data-testid="inquiry-success">
              <CheckCircle2 size={22} className="mb-4 text-[#FF4D00]" />
              <h2 className="text-base font-bold sm:text-lg">Message received.</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Thanks for reaching out — I've got your details and will reply soon.
              </p>
              <button
                data-testid="inquiry-send-another"
                onClick={() => setSent(false)}
                className="link-underline mt-6 text-sm text-[#FF4D00]"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={submitInquiry} className="space-y-5" data-testid="inquiry-form">
              <div>
                <label className="font-mono-label mb-2 block">Name</label>
                <input required value={form.name} onChange={set("name")} className={field} data-testid="inquiry-name" />
              </div>
              <div>
                <label className="font-mono-label mb-2 block">Email</label>
                <input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="inquiry-email" />
              </div>
              <div>
                <label className="font-mono-label mb-2 block">Project type</label>
                <select required value={form.project_type} onChange={set("project_type")} className={field} data-testid="inquiry-project-type">
                  <option value="">Select a type</option>
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono-label mb-2 block">Budget range</label>
                <select required value={form.budget_range} onChange={set("budget_range")} className={field} data-testid="inquiry-budget">
                  <option value="">Select a range</option>
                  {BUDGETS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono-label mb-2 block">Message</label>
                <textarea required rows={5} value={form.message} onChange={set("message")} className={field} data-testid="inquiry-message" />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="inquiry-submit"
                className="inline-flex items-center gap-2 rounded-sm bg-[#FF4D00] px-6 py-3 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                {sending && <Loader2 size={16} className="animate-spin" />}
                Send inquiry
              </button>
            </form>
          )}
        </section>

        <section id="book-a-call" data-testid="booking-section">
          <div className="mb-8 flex items-center gap-3">
            <Clock size={16} className="text-[#FF4D00]" />
            <p className="font-mono-label">Book a call · 30 min · {tz}</p>
          </div>

          {confirmed ? (
            <div className="border border-border bg-card p-8" data-testid="booking-success">
              <CheckCircle2 size={22} className="mb-4 text-[#FF4D00]" />
              <h2 className="text-base font-bold sm:text-lg">Call confirmed</h2>
              <p className="mt-3 text-sm text-muted-foreground">
                {confirmed.slot_date} at {confirmed.slot_time} {tz} — a confirmation email is on its way to{" "}
                {confirmed.email}.
              </p>
              <button
                data-testid="booking-book-another"
                onClick={() => setConfirmed(null)}
                className="link-underline mt-6 text-sm text-[#FF4D00]"
              >
                Book another slot
              </button>
            </div>
          ) : (
            <div className="border border-border bg-card">
              <div className="flex gap-2 overflow-x-auto border-b border-border p-4" data-testid="booking-days">
                {days.map((d) => (
                  <button
                    key={d.date}
                    onClick={() => {
                      setActiveDay(d.date);
                      setSlot(null);
                    }}
                    data-testid={`booking-day-${d.date}`}
                    className={`min-w-[76px] shrink-0 rounded-sm border px-3 py-2 text-center transition-colors ${
                      activeDay === d.date
                        ? "border-[#FF4D00] bg-[#FF4D00] text-white"
                        : "border-border hover:border-foreground"
                    }`}
                  >
                    <span className="block text-[10px] uppercase tracking-widest opacity-70">{d.weekday}</span>
                    <span className="block text-sm font-medium">{d.label}</span>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2 p-4 sm:grid-cols-4" data-testid="booking-slots">
                {day?.slots.length ? (
                  day.slots.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSlot(s)}
                      data-testid={`booking-slot-${s}`}
                      className={`rounded-sm border py-2 text-sm transition-colors ${
                        slot === s ? "border-[#FF4D00] bg-[#FF4D00] text-white" : "border-border hover:border-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))
                ) : (
                  <p className="col-span-full py-6 text-sm text-muted-foreground" data-testid="booking-no-slots">
                    No open slots that day.
                  </p>
                )}
              </div>

              <form onSubmit={submitBooking} className="space-y-4 border-t border-border p-4" data-testid="booking-form">
                <input
                  required
                  placeholder="Your name"
                  value={booking.name}
                  onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                  className={field}
                  data-testid="booking-name"
                />
                <input
                  required
                  type="email"
                  placeholder="Your email"
                  value={booking.email}
                  onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                  className={field}
                  data-testid="booking-email"
                />
                <input
                  placeholder="What should we cover? (optional)"
                  value={booking.topic}
                  onChange={(e) => setBooking({ ...booking, topic: e.target.value })}
                  className={field}
                  data-testid="booking-topic"
                />
                <button
                  type="submit"
                  disabled={bookingBusy || !slot}
                  data-testid="booking-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-sm border border-foreground px-6 py-3 text-sm font-medium transition-colors hover:bg-foreground hover:text-background disabled:opacity-50"
                >
                  {bookingBusy && <Loader2 size={16} className="animate-spin" />}
                  {slot ? `Confirm ${slot} ${tz}` : "Pick a slot to continue"}
                </button>
              </form>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
